import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import { withStyles } from 'theme/withStyles';
import visuallyHidden from 'styles/visuallyHidden';

const styles = ({ palette, spacing, typography }) => ({
  field: {
    textAlign: ({ center }) => (center ? 'center' : undefined),
  },
  group: {},
  page: {},
  root: {
    ...typography.p,
    ...typography.p3,
    '& *': {
      display: 'inline',
      marginBottom: 0,
      paddingBottom: 0,
    },
    '&$field': {
      fontSize: 12,
      marginTop: spacing.unit * 2,
    },
    '&$group, &$page': {
      fontSize: typography.p2.fontSize,
      padding: [spacing.sm, 0],
      textAlign: 'center',
    },
    color: palette.error,
    display: 'block',
  },
  visuallyHidden,
});

function getStaticClassName(type) {
  switch (type) {
    case 'page':
      return 'page-error';
    case 'group':
      return 'group-error';
    case 'field':
    default:
      return 'field-error';
  }
}

const ErrorMsg = ({ classes, error, errorMsg, name, style, type }) => {
  if (type === 'field' && !error) {
    return null;
  }

  // Page errors have extra HTML we cannot remove
  switch (type) {
    case 'page':
      return (
        <div
          className={classnames('row', {
            [classes.visuallyHidden]: !error,
            hidden: !error,
          })}
        >
          <div className="widget text-center" role="alert" aria-live="assertive">
            <RawHtml
              type="span"
              className={classnames('error-msg', getStaticClassName(type), classes.root, {
                [classes.page]: type === 'page',
                [classes.group]: type === 'group',
                [classes.field]: type === 'field',
              })}
              style={style}
            >
              {errorMsg}
            </RawHtml>
          </div>
        </div>
      );
    case 'group':
      return (
        <div
          role="alert"
          aria-live="assertive"
          className={classnames('row', {
            [classes.visuallyHidden]: !error,
            hidden: !error,
          })}
        >
          <RawHtml
            type="span"
            className={classnames('error-msg', getStaticClassName(type), classes.root, {
              [classes.page]: type === 'page',
              [classes.group]: type === 'group',
              [classes.field]: type === 'field',
            })}
            style={style}
          >
            {errorMsg}
          </RawHtml>
        </div>
      );
    case 'field':
    default:
      return (
        <RawHtml
          type="span"
          id={`error-${name}`}
          className={classnames('error-msg', getStaticClassName(type), classes.root, {
            [classes.page]: type === 'page',
            [classes.group]: type === 'group',
            [classes.field]: type === 'field',
          })}
          style={style}
        >
          {errorMsg}
        </RawHtml>
      );
  }
};

ErrorMsg.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool,
  errorMsg: PropTypes.string,
  name: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.string),
  type: PropTypes.string,
};

ErrorMsg.defaultProps = {
  error: false,
  errorMsg: '',
  name: '',
  style: null,
  type: 'field',
};

const ErrorMsgStyled = withStyles(styles)(ErrorMsg);

const mapStateToProps = ({ fields, groups, page }, { error, errorMsg, name, type }) => {
  switch (type) {
    case 'page':
      return {
        error: error ?? page.error,
        errorMsg: errorMsg ?? page.errorMsg,
      };
    case 'group':
      return {
        error: error ?? groups[name]?.error,
        errorMsg: errorMsg ?? groups[name]?.errorMsg,
      };
    case 'field':
    default:
      // This field does not exist in state - it is created on the doc upload page. It is also impossible to get an error here.
      if (name === 'document_upload_category_lookup') {
        return {};
      }

      return {
        error: error ?? fields[name]?.error,
        errorMsg: errorMsg ?? fields[name]?.errorMsg,
      };
  }
};

export default connect(mapStateToProps)(ErrorMsgStyled);
